import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useEffect, ReactNode } from 'react'
import { UserFoldersActions } from '../store/slice'
import {
  selectCurrentFolderId,
  selectActiveRootFolderId,
  selectIsInitializingFromUrl,
  selectUrlInitializationError,
  selectFolderDataById,
  selectRootFolderRequestForMatter,
  selectIsCurrentFolderDriveHidden,
} from '../store/selectors'
import { kMattersRoute } from '@/matter/constants'
import { CircularProgress } from '@mui/material'
import { matterPaths } from '@/routing/paths'
import ErrorCard from '@/components/error/ErrorCard'

interface Props {
  children: ReactNode
}

/**
 * UserFilesFolderRouteHandler
 *
 * Ensures folder-based navigation loads with correct folder context.
 * URL is the single source of truth - one-way sync from URL → Redux only.
 * - On initial load, syncs `folderId` from URL into Redux (or uses root if not specified)
 * - Handles invalid folder IDs with route recovery
 *
 * Pattern matches MatterRouteHandler for consistency.
 */
export function UserFilesFolderRouteHandler({ children }: Props) {
  const { matterId: urlMatterId, folderId: urlFolderId } = useParams<{ matterId: string; folderId?: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Redux state
  const activeMatterId = useAppSelector((state) => state.mattersState.activeMatterId)
  const currentFolderId = useAppSelector(selectCurrentFolderId)
  const activeRootFolderId = useAppSelector(selectActiveRootFolderId)
  const urlFolder = useAppSelector((state) => selectFolderDataById(state, { folderId: urlFolderId }))
  const isInitializingFromUrl = useAppSelector(selectIsInitializingFromUrl)
  const urlInitializationError = useAppSelector(selectUrlInitializationError)
  const isCurrentFolderDriveHidden = useAppSelector(selectIsCurrentFolderDriveHidden)

  // Matter ID from URL - required for user files routes
  const desiredMatterId = urlMatterId ?? null
  const rootFolderRequest = useAppSelector((state) => selectRootFolderRequestForMatter(state, { matterId: desiredMatterId }))
  const isMatterSettled = activeMatterId === desiredMatterId
  const rootFolderErrorStatusCode = rootFolderRequest.statusCode
  const isRootFolderMissingOrDenied = rootFolderErrorStatusCode === 403 || rootFolderErrorStatusCode === 404
  const shouldRedirectHiddenFolder =
    isMatterSettled &&
    desiredMatterId !== null &&
    activeRootFolderId !== null &&
    currentFolderId !== null &&
    currentFolderId !== activeRootFolderId &&
    isCurrentFolderDriveHidden

  /**
   * Matter -> Folders: fetch the matter root folder only once the matter URL + state are settled.
   *
   * Note: This gate keeps UserFiles components from mounting before MatterRouteHandler sync completes.
   */
  useEffect(() => {
    if (!isMatterSettled) return

    // User files require matter context - redirect to matters table if missing
    if (!desiredMatterId) {
      if (currentFolderId) {
        dispatch(UserFoldersActions.setCurrentFolder({ folderId: null }))
      }
      navigate(kMattersRoute, { replace: true })
      return
    }

    if (!activeRootFolderId) {
      dispatch(UserFoldersActions.fetchRootFolder({ matterId: desiredMatterId }))
    }
  }, [isMatterSettled, desiredMatterId, activeRootFolderId, currentFolderId, navigate, dispatch])

  /**
   * URL -> Redux: resolve the folder context once matter + root are available.
   */
  useEffect(() => {
    if (!isMatterSettled) return
    if (isInitializingFromUrl) return
    if (!desiredMatterId) return
    if (!activeRootFolderId) return

    if (urlFolderId) {
      if (urlFolder && urlFolder.matter_id !== desiredMatterId) {
        // desiredMatterId is guaranteed non-null here due to early return above
        navigate(matterPaths.userFiles.root(desiredMatterId), { replace: true })
        return
      }

      if (currentFolderId === urlFolderId) {
        // If folder data is missing (e.g. cleared from state), re-initialize from URL.
        if (!urlFolder) {
          dispatch(UserFoldersActions.initializeFromUrl({ folderId: urlFolderId }))
        }
        return
      }

      if (urlFolder) {
        dispatch(UserFoldersActions.setCurrentFolder({ folderId: urlFolderId }))
      } else {
        dispatch(UserFoldersActions.initializeFromUrl({ folderId: urlFolderId }))
      }
      return
    }

    if (currentFolderId !== activeRootFolderId) {
      dispatch(UserFoldersActions.setCurrentFolder({ folderId: activeRootFolderId }))
    }
  }, [isMatterSettled, desiredMatterId, urlMatterId, urlFolderId, urlFolder, activeRootFolderId, currentFolderId, isInitializingFromUrl, navigate, dispatch])

  // Do not allow users to directly navigate to external storage folders
  useEffect(() => {
    if (!shouldRedirectHiddenFolder || !desiredMatterId) return

    navigate(matterPaths.userFiles.root(desiredMatterId), { replace: true })
  }, [shouldRedirectHiddenFolder, desiredMatterId, navigate])

  /**
   * Handle URL initialization failure for the currently requested URL folder.
   */
  useEffect(() => {
    if (!urlFolderId || !urlInitializationError) return
    if (urlInitializationError.folderId !== urlFolderId) return
    if (!urlMatterId) return // Guard for TypeScript - should never happen on user files routes

    navigate(matterPaths.userFiles.root(urlMatterId), { replace: true })
    dispatch(UserFoldersActions.clearUrlInitializationError())
  }, [urlFolderId, urlInitializationError, urlMatterId, navigate, dispatch])

  const isReady = (() => {
    if (!isMatterSettled) return false
    if (!desiredMatterId) return true
    if (!activeRootFolderId) return false
    if (isInitializingFromUrl) return false
    if (shouldRedirectHiddenFolder) return false

    if (urlFolderId) {
      return currentFolderId === urlFolderId && Boolean(urlFolder)
    }

    return currentFolderId === activeRootFolderId
  })()

  if (desiredMatterId && isMatterSettled && rootFolderRequest.status === 'error') {
    if (isRootFolderMissingOrDenied) {
      return (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-6">
          <ErrorCard
            code="404"
            title="Matter Files are not available"
            message="This matter may have been removed, deleted, or is not available to your account."
            actionLabel="Back to Matters"
            onClick={() => navigate(kMattersRoute)}
          />
        </div>
      )
    }

    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-6">
        <ErrorCard
          code={rootFolderErrorStatusCode ? String(rootFolderErrorStatusCode) : '500'}
          title="Something went wrong on our end"
          message="We hit an unexpected error loading this page. Please try again."
          actionLabel="Retry"
          onClick={() => dispatch(UserFoldersActions.fetchRootFolder({ matterId: desiredMatterId, force: true }))}
        />
      </div>
    )
  }

  if (!isReady) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <CircularProgress size={24} />
      </div>
    )
  }

  return <>{children}</>
}
